/**
 * WizardFrame (spec 2026-09-30, 6.2, 7 and 9): the frame of one step of a guided flow. The
 * counter "Langkah 3 dari 7" in a polite live region at the top, a plain heading that takes
 * the focus whenever the step changes, one sentence of help, the step's fields, then
 * Kembali (outline), any quiet actions of the flow, and Lanjut (the screen's red button).
 * Fields and buttons sit in the form wrapper (sapuan isian 3.9): Enter moves to the next field
 * and is Lanjut from the last; a Lanjut with problems shows the summary above the fields. A
 * step that checks outside a submit (Pendaftaran's quiet Simpan) raises `mintaRingkasan`. A
 * failed save's sentence shows under the buttons.
 * While a Lanjut runs both buttons are `aria-disabled`, drawn as disabled but never
 * `disabled`: a disabled button drops the focus to <body>, so a Lanjut that waits and does
 * not move on (a duplicate warning, a failed save) would send Tab back to the top. A press
 * then does nothing (the guards below). A quiet action the flow adds does the same, with
 * `SIBUK` for its look.
 * Tambah umat uses it as it is; Pendaftaran's guided mode (plan 1c) adds its quiet Simpan
 * (`aksiLain`), names its Periksa page "Langkah terakhir" (`penghitung`), widens the
 * Kartu Keluarga grid (`className`) and moves the focus by step and member (`kunciFokus`),
 * not by a title that a save or a sync renames in place. Later flows build on it. While a step
 * is on screen the flow's saves are held a while before they are sent (app/alurTerbuka).
 */
import { uiStrings } from '@satu/shared'
import { type ReactNode, useEffect, useId, useRef } from 'react'
import { bukaAlur } from '@/app/alurTerbuka'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { FormIsian } from './FormIsian'
import { teksLangkahKe } from './wizardTeks'

const teks = uiStrings.wizard
/** The Button's disabled look for `aria-disabled`, which keeps the focus. */
export const SIBUK = 'aria-disabled:pointer-events-none aria-disabled:opacity-50'

export interface WizardFrameProps {
  judul: string
  petunjuk?: string
  langkah: number
  jumlah: number
  /** Replaces "Langkah n dari m" (a page after the counted steps). */
  penghitung?: string
  onKembali?: () => void
  /**
   * Validate and save. A step may return the save's promise: the summary of problems takes
   * the focus once it settles (`FormIsian`).
   */
  onLanjut: () => unknown
  lanjutLabel?: string
  /**
   * Quiet actions between Kembali and Lanjut; never a second red button. While busy each is
   * `aria-disabled` with the `SIBUK` look and a guard, as Kembali and Lanjut are.
   */
  aksiLain?: ReactNode
  sibuk?: boolean
  galat?: string | null
  /** Merged onto the section (a step that needs the full width passes `max-w-none`). */
  className?: string
  /** When this changes the heading takes the focus; by default the step or its title. */
  kunciFokus?: string
  /** Raised by a check run without a submit (a quiet Simpan): the summary shows, focused. */
  mintaRingkasan?: number | undefined
  children: ReactNode
}

export function WizardFrame({
  judul,
  petunjuk,
  langkah,
  jumlah,
  penghitung,
  onKembali,
  onLanjut,
  lanjutLabel,
  aksiLain,
  sibuk = false,
  galat = null,
  className,
  kunciFokus,
  mintaRingkasan,
  children,
}: WizardFrameProps) {
  const headingId = useId()
  const judulRef = useRef<HTMLHeadingElement>(null)
  const fokus = kunciFokus ?? `${langkah}|${judul}`
  // Release 17 review M-4: while a flow's step is on screen its saves wait a while before they
  // are sent, so a value corrected a moment later travels once (../../sync/loop).
  useEffect(() => bukaAlur(), [])

  // biome-ignore lint/correctness/useExhaustiveDependencies: the heading takes the focus when the step changes, not on every render
  useEffect(() => {
    judulRef.current?.focus()
  }, [fokus])

  return (
    <section
      aria-labelledby={headingId}
      className={cn('flex max-w-[44rem] flex-col gap-6', className)}
    >
      <div className="flex flex-col gap-1">
        <p
          role="status"
          aria-live="polite"
          aria-atomic="true"
          className="text-label font-medium text-muted num"
        >
          {penghitung ?? teksLangkahKe(langkah, jumlah)}
        </p>
        <h2
          id={headingId}
          ref={judulRef}
          tabIndex={-1}
          className="text-heading font-semibold text-text outline-none"
        >
          {judul}
        </h2>
        {petunjuk !== undefined ? (
          <p className="measure font-prose text-prose text-muted">{petunjuk}</p>
        ) : null}
      </div>
      <FormIsian
        aria-busy={sibuk ? true : undefined}
        className="flex flex-col gap-6"
        mintaRingkasan={mintaRingkasan}
        onSubmit={() => (sibuk ? undefined : onLanjut())}
      >
        {children}
        <div className="flex flex-wrap items-center gap-2 border-t border-hairline pt-4">
          {onKembali !== undefined ? (
            <Button
              type="button"
              variant="outline"
              className={`press ${SIBUK}`}
              aria-disabled={sibuk || undefined}
              onClick={() => {
                if (!sibuk) onKembali()
              }}
            >
              {teks.kembali}
            </Button>
          ) : null}
          {aksiLain}
          <Button
            type="submit"
            className={`press ml-auto ${SIBUK}`}
            aria-disabled={sibuk || undefined}
          >
            {lanjutLabel ?? teks.lanjut}
          </Button>
          {galat !== null ? (
            <p role="alert" className="basis-full text-label text-text">
              {galat}
            </p>
          ) : null}
        </div>
      </FormIsian>
    </section>
  )
}
